// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Touch input (M3-33, 08 §5.2).
//
// | Action | Gesture |
// |---|---|
// | Left / right flipper | Touch and hold anywhere in the left / right zone. **Multi-touch**, so both work at once |
// | Plunge | Touch the plunger's column, drag down to tension, release |
// | Nudge | Flick anywhere on the playfield; direction and speed give the impulse |
//
// **A touch's role is decided once, on touch-down, and held for its whole life.** A finger that
// starts on the left flipper and slides into the right zone is still holding the left flipper:
// a player rolling a thumb along the bottom of the screen is not asking to swap hands, and a role
// that changed under a moving finger would make a held bat release itself mid-shot. It is also
// what makes the flick unambiguous — a touch that began on the playfield is a nudge and can never
// become a flipper, however far it travels.
//
// **Releasing a flipper is a count, not a flag.** Two fingers in the left zone and lifting one
// must not drop the bat. Every real multi-touch bug in this class looks the same from outside:
// the flipper works, and then occasionally does not, and only for people who play with their
// whole hand on the glass.
//
// The zones themselves are `src/client/phone.ts`, shared with what draws them. That sharing used
// to matter because the boundary was *invisible* after the first ball; since M3-49 it matters more,
// because the band draws real cabinet buttons and a listener testing different rectangles would put
// a button in one place and its hit area in another, permanently and visibly.

import type { PhoneLayout, TouchRole } from '../client/phone'
import { roleAt } from '../client/phone'
import { NUDGE_IMPULSE, PLUNGE_MIN, type PlayControls } from './desktop'

/**
 * How far down the plunger must be dragged for full power, in CSS pixels.
 *
 * **The ideal rather than the requirement**, because on a phone there is often not this much screen
 * left below the thumb — see `fullDrawDistance`.
 */
export const PLUNGE_DRAG_PX = 120

/**
 * …and the shortest drag that may ever mean full power (M3-33a).
 *
 * A floor is needed because the requirement now adapts to the room available, and without one a
 * touch that began five pixels above the bottom edge would reach full power in five pixels — a
 * hair trigger exactly where a thumb is most likely to land by accident. Below this much room,
 * full power is simply not reachable from that starting point, which is the honest answer: the
 * player did not pull the plunger far, so the ball does not go far.
 */
export const PLUNGE_DRAG_MIN_PX = 48

/**
 * How far *this* drag has to travel for full power.
 *
 * **The fixed 120 px was unreachable on a phone, and the arithmetic said so** (M3-33a). The plunger
 * was then a quarter-disc centred on the bottom-right corner of the stage, so a touch inside it was
 * at most a radius — about 132 px on a 390 px phone — above the bottom edge, which is where a
 * downward drag runs out of screen. Full power needed 120 of those 132, reachable only by starting
 * within about twelve pixels of the arc's top boundary. Every ordinary thumb placement topped out
 * near half, with nothing on screen to explain why.
 *
 * So the requirement is the room the player actually has: full power is a drag to the bottom of the
 * stage, or `PLUNGE_DRAG_PX`, whichever is shorter, and never less than `PLUNGE_DRAG_MIN_PX`.
 *
 * ***This is still what decides the power, and M3-49 is what made it consistent.*** The adaptive
 * rule fixed the ceiling but left the power a function of where the thumb happened to land: a touch
 * near the top of the arc had 130 px of room and a touch near the bottom had 20, so the same
 * gesture meant different things on the same ball. The plunger now has a **drawn knob**, resting at
 * the top of its column, and a player who grabs the knob has the column's full height below them
 * every time. The adaptive floor stays for the player who grabs the rod half-way down instead —
 * which is a shorter pull, and should be a weaker shot.
 */
export function fullDrawDistance(availableBelow: number): number {
  if (!Number.isFinite(availableBelow)) return PLUNGE_DRAG_PX
  return clamp(availableBelow, PLUNGE_DRAG_MIN_PX, PLUNGE_DRAG_PX)
}

/**
 * How far down the column counts as having *learned* the plunger (M3-51).
 *
 * A quarter of the draw. Low enough that a genuine, timid first pull retires the arrow — a player
 * who has done the gesture does not need to be shown it again — and high enough that a tap, a
 * thumb resting on the ball, or two pixels of tremble does not. Getting this wrong in the generous
 * direction is worse than in the strict one: an arrow that outstays its welcome is a nag, and one
 * that leaves before the lesson lands leaves the player exactly where they started.
 */
export const TAUGHT_DRAW = 0.25

/** A flick has to be at least this long to be a nudge rather than a tap. */
export const FLICK_MIN_PX = 24

/** …and over within this, or it is a drag and the player was doing something else. */
export const FLICK_MAX_MS = 400

/** The speed, in px/ms, at which a flick is a full-strength nudge. */
export const FLICK_FULL_SPEED = 2.2

/** The weakest and strongest a flick can be, as multiples of one key-press of nudge. */
export const FLICK_MIN_SCALE = 0.35
export const FLICK_MAX_SCALE = 1.4

interface LiveTouch {
  readonly role: TouchRole
  readonly startX: number
  readonly startY: number
  readonly startedAt: number
  /**
   * How tall the surface was when this touch went down.
   *
   * Captured per touch rather than read at release, because it is only ever used with `startY` —
   * the pair answers "how much screen was below the finger", and reading one of them later would
   * be answering it about a stage that may since have been resized by a rotation or a keyboard.
   */
  readonly surfaceHeight: number
  x: number
  y: number
}

/** The parts of a `TouchEvent` this reads, so a test can send one without a browser. */
export interface TouchLike {
  readonly identifier: number
  readonly clientX: number
  readonly clientY: number
}

export interface TouchEventLike {
  readonly changedTouches: ArrayLike<TouchLike>
  readonly target?: TouchTarget | null
  preventDefault?: () => void
}

/**
 * The one thing this needs of an event's target: what it sits inside.
 *
 * Declared as its own tiny shape rather than as `Element` so a test can send a touch that began on
 * a button without standing up a DOM.
 */
export interface TouchTarget {
  closest?: (selectors: string) => unknown
}

/**
 * Anything the player presses *as a control* rather than as a piece of the machine.
 *
 * A touch that begins on one of these is not a gesture at the playfield, and the machine must keep
 * its hands off it — see `beganOnAControl`.
 */
const CONTROL_SELECTOR =
  'button, a[href], input, select, textarea, label, [role="button"], [role="link"], [role="slider"], [role="switch"]'

/**
 * Did this touch begin on a control rather than on the machine?
 *
 * **This is what keeps the phone's primary button working.** The stage's own controls — the
 * plunger button, Pause, the VR entry, the seat picker — are children of the surface these
 * listeners are attached to. A browser only synthesises the `click` that fires their `onClick` if
 * nothing called `preventDefault` on the `touchstart`; the flipper and nudge handling below calls
 * it on every touch, so without this check a tap on "PULL THE PLUNGER" registered as a press,
 * registered as a release, and did nothing at all. It was invisible to every test that used a
 * mouse and to every developer who was not holding a phone.
 */
const beganOnAControl = (e: TouchEventLike): boolean => {
  const target = e.target
  if (target === null || target === undefined) return false
  return target.closest?.(CONTROL_SELECTOR) != null
}

/** What the listener attaches to. A canvas or its wrapper, in the product. */
export interface TouchSurface {
  // `AddEventListenerOptions` rather than `unknown`, so a real `HTMLElement` satisfies this: the
  // DOM's own overload is not assignable to a signature whose options are wider than its own.
  addEventListener: (
    type: string,
    listener: (event: Event) => void,
    options?: boolean | AddEventListenerOptions,
  ) => void
  removeEventListener: (
    type: string,
    listener: (event: Event) => void,
    options?: boolean | EventListenerOptions,
  ) => void
  getBoundingClientRect?: () => { left: number; top: number; width: number; height: number }
}

export interface TouchControlsOptions {
  /** The same object the keyboard mutates. A player may use both, and some do. */
  readonly controls: PlayControls
  /** Where the zones are, read fresh: a phone rotates and a keyboard opens. */
  readonly layout: () => PhoneLayout
  /** Is there a ball in the shooter lane? Decides whether the plunger column is live at all. */
  readonly plungerAvailable: () => boolean
  /** Told how far the plunger is drawn, 0…1, so the knob can follow the thumb. */
  readonly onPlungerDraw?: (power: number) => void
  /**
   * The plunger was released (M3-51).
   *
   * **Given the release rather than setting `controls.launch` here**, because the host knows
   * something this listener cannot: whether the machine is in attract, where the launch control is
   * the *start* button and one press has to do two things — start the game, then fire the ball it
   * seats. `MachineRuntime.launch` already carries that (`pendingLaunch`), and routing the drag
   * through it is what lets a phone player start a game by pulling the plunger instead of by
   * pressing a button laid over the machine.
   *
   * Without a handler this falls back to setting the controls directly, which is what the harness
   * wants: it has a `Game` with a ball already in the lane and no attract to get out of.
   */
  readonly onPlunge?: (power: number) => void
  /**
   * The first time the plunger was properly *pulled*, so the arrow teaching it can retire.
   *
   * **Not the first touch of it, and not a tap.** A tap fires the plunger weakly and proves the
   * player found it by accident as often as on purpose; the gesture being taught is the drag. See
   * `TAUGHT_DRAW` for where the line is.
   */
  readonly onFirstPlunge?: () => void
  /**
   * Told the first time the player touches the machine at all.
   *
   * **This is not the hint's signal, and the difference matters.** What this answers is "was this
   * game played with a finger", which decides the `inputMethod` on the score row — and a game
   * played with the plunger button, nudges and no flipper is still a game played with a finger.
   */
  readonly onFirstTouch?: () => void
  /**
   * Told the first time the player presses a **flipper**, so the first-run hint can retire.
   *
   * The hint used to retire on `onFirstTouch`, which spent it on taps that taught nobody anything.
   * See the call site.
   */
  readonly onFirstFlip?: () => void
  readonly now?: () => number
}

/**
 * Attach the touch controls, and return how to take them off again.
 *
 * `passive: false` on every listener, and `preventDefault` on the ones that matter. Without it a
 * flick scrolls the page, a second finger pinch-zooms the machine, and a double tap zooms it
 * again and stays there — none of which a player can undo without leaving the game.
 */
export function attachTouchControls(
  surface: TouchSurface,
  options: TouchControlsOptions,
): { detach: () => void } {
  const { controls } = options
  const clock = options.now ?? (() => performance.now())
  const live = new Map<number, LiveTouch>()
  let toldAboutFirstTouch = false
  let toldAboutFirstFlip = false
  let toldAboutFirstPlunge = false

  const localPoint = (touch: TouchLike): { x: number; y: number } => {
    const rect = surface.getBoundingClientRect?.()
    return { x: touch.clientX - (rect?.left ?? 0), y: touch.clientY - (rect?.top ?? 0) }
  }

  /** Hold the bats down while any touch says so. A count, not a flag — see the header. */
  const applyFlippers = (): void => {
    let left = false
    let right = false
    for (const touch of live.values()) {
      if (touch.role === 'leftFlipper') left = true
      if (touch.role === 'rightFlipper') right = true
    }
    controls.leftFlipper = left
    controls.rightFlipper = right
  }

  const reportDraw = (): void => {
    const drawing = [...live.values()].find((t) => t.role === 'plunger')
    options.onPlungerDraw?.(drawing === undefined ? 0 : drawPower(drawing))
  }

  const onStart = (event: Event): void => {
    const e = event as unknown as TouchEventLike
    // A touch on a control belongs to the control. Nothing is registered, nothing is prevented,
    // and the browser goes on to synthesise the click the button is waiting for.
    if (beganOnAControl(e)) return
    const layout = options.layout()
    const plungerLive = options.plungerAvailable()
    const surfaceHeight = surface.getBoundingClientRect?.()?.height ?? 0
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!
      const { x, y } = localPoint(touch)
      const role = roleAt(layout, plungerLive, x, y)
      live.set(touch.identifier, { role, startX: x, startY: y, startedAt: clock(), surfaceHeight, x, y })
      if (role === 'plunger') controls.chargeStartedAt = clock()
      // **A flipper, not any touch** (M3-33a). This retires the only affordance a phone player ever
      // gets, and 08 §5.2 says it lasts "for the first ball of a player's first game" — so firing
      // it on the first `touchstart` of any kind meant a stray tap during the attract, a nudge
      // flick, or a hand resting on the glass spent it before the player had flipped anything.
      // Permanently, per browser, with nothing to bring it back.
      //
      // A flipper press is the one touch that proves the hint was read, which is the only event
      // that should be allowed to end it.
      if (!toldAboutFirstFlip && (role === 'leftFlipper' || role === 'rightFlipper')) {
        toldAboutFirstFlip = true
        options.onFirstFlip?.()
      }
    }
    // Any touch at all, and separately, because this one answers a different question: whether the
    // score row says this game was played with a finger.
    if (!toldAboutFirstTouch) {
      toldAboutFirstTouch = true
      options.onFirstTouch?.()
    }
    applyFlippers()
    reportDraw()
    e.preventDefault?.()
  }

  const onMove = (event: Event): void => {
    const e = event as unknown as TouchEventLike
    let ours = false
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!
      const held = live.get(touch.identifier)
      if (held === undefined) continue
      ours = true
      const { x, y } = localPoint(touch)
      held.x = x
      held.y = y
    }
    reportDraw()
    // A moving finger must not scroll the page under the machine, and two of them must not
    // pinch-zoom it — but only for the fingers this is actually driving. A finger that came down
    // on a button is the button's, and a drag off it is the browser's business to cancel.
    if (ours) e.preventDefault?.()
  }

  const onEnd = (event: Event): void => {
    const e = event as unknown as TouchEventLike
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!
      const held = live.get(touch.identifier)
      if (held === undefined) continue
      live.delete(touch.identifier)
      if (held.role === 'plunger') releasePlunger(held)
      else if (held.role === 'playfield') maybeNudge(held)
    }
    applyFlippers()
    reportDraw()
  }

  /**
   * A touch that has been cancelled is not a gesture the player finished.
   *
   * The browser cancels touches when a system gesture takes over — the notification shade, the
   * app switcher, an incoming call. Firing a plunge or a nudge on the way out would put the ball
   * in play as the player swiped away from the game, and they would come back to a drained ball
   * they never shot.
   */
  const onCancel = (event: Event): void => {
    const e = event as unknown as TouchEventLike
    for (let i = 0; i < e.changedTouches.length; i++) {
      live.delete(e.changedTouches[i]!.identifier)
    }
    controls.chargeStartedAt = null
    applyFlippers()
    reportDraw()
  }

  const releasePlunger = (held: LiveTouch): void => {
    controls.chargeStartedAt = null
    const power = drawPower(held)
    if (options.onPlunge !== undefined) options.onPlunge(power)
    else {
      controls.launchPower = power
      controls.launch = true
    }
    // **A pull, not a touch and not a tap.** The arrow this retires is teaching one thing — drag
    // the ball down — and a tap fires the plunger without having learned it. Measured on the draw
    // rather than on the pixels, so the same fraction of the same column means the same thing on
    // every screen.
    if (!toldAboutFirstPlunge && power >= PLUNGE_MIN + (1 - PLUNGE_MIN) * TAUGHT_DRAW) {
      toldAboutFirstPlunge = true
      options.onFirstPlunge?.()
    }
  }

  const maybeNudge = (held: LiveTouch): void => {
    const dx = held.x - held.startX
    const dy = held.y - held.startY
    const distance = Math.sqrt(dx * dx + dy * dy)
    if (distance < FLICK_MIN_PX) return
    const elapsed = clock() - held.startedAt
    // A slow drag across the playfield is somebody steadying the phone, not shoving it.
    if (elapsed > FLICK_MAX_MS || elapsed <= 0) return

    const speed = distance / elapsed
    const scale = clamp(speed / FLICK_FULL_SPEED, FLICK_MIN_SCALE, FLICK_MAX_SCALE)
    const magnitude = NUDGE_IMPULSE * scale
    // Screen y runs down and the playfield's runs up the table, so a flick *up* the screen shoves
    // the machine *up* the playfield. Getting this backwards is the one nudge bug a player cannot
    // diagnose: it works, it is just the wrong way round, and it feels like bad physics.
    controls.nudges.push([(dx / distance) * magnitude, (-dy / distance) * magnitude])
  }

  const listeners: [string, (event: Event) => void][] = [
    ['touchstart', onStart],
    ['touchmove', onMove],
    ['touchend', onEnd],
    ['touchcancel', onCancel],
  ]
  for (const [type, listener] of listeners) {
    surface.addEventListener(type, listener, { passive: false })
  }

  return {
    detach: () => {
      for (const [type, listener] of listeners) surface.removeEventListener(type, listener)
      live.clear()
      controls.leftFlipper = false
      controls.rightFlipper = false
      controls.chargeStartedAt = null
    },
  }
}

/**
 * How far a plunger drag has drawn the plunger, 0…1.
 *
 * Downwards only, and the floor is `PLUNGE_MIN` rather than zero: on a real machine a plunger
 * released from rest still fires — weakly — and a touch that registered as a plunge and then did
 * nothing would leave the ball sitting in the lane with the arc gone.
 */
export function drawPower(touch: { startY: number; y: number; surfaceHeight?: number }): number {
  // The room below where the finger went down. `undefined` is a caller that does not know — a test
  // sending two coordinates — and it gets the old fixed distance, which is the right answer when
  // there is no screen edge in the question.
  const available =
    touch.surfaceHeight === undefined ? PLUNGE_DRAG_PX : touch.surfaceHeight - touch.startY
  const drawn = clamp((touch.y - touch.startY) / fullDrawDistance(available), 0, 1)
  return PLUNGE_MIN + (1 - PLUNGE_MIN) * drawn
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
